import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, ShieldAlert, Pin, PinOff, Lock, Unlock, Pencil, Trash2, Ban, FolderPlus, FolderX, UserCheck } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import InternalAccessGuard from '../components/InternalAccessGuard';
import { fullDate } from '../components/forum/forumUtils';

const ACTION_META = {
  pin:             { label: 'Fijó un hilo', icon: Pin, color: 'text-amber-600 bg-amber-50' },
  unpin:           { label: 'Desfijó un hilo', icon: PinOff, color: 'text-slate-500 bg-slate-100' },
  lock:            { label: 'Cerró un hilo', icon: Lock, color: 'text-charcoal-600 bg-charcoal-100' },
  unlock:          { label: 'Reabrió un hilo', icon: Unlock, color: 'text-emerald-600 bg-emerald-50' },
  edit_post:       { label: 'Editó una publicación', icon: Pencil, color: 'text-cmic-600 bg-cmic-50' },
  edit_thread:     { label: 'Editó un hilo', icon: Pencil, color: 'text-cmic-600 bg-cmic-50' },
  delete_post:     { label: 'Eliminó una publicación', icon: Trash2, color: 'text-rose-600 bg-rose-50' },
  delete_thread:   { label: 'Eliminó un hilo', icon: Trash2, color: 'text-rose-600 bg-rose-50' },
  block:           { label: 'Bloqueó a un usuario', icon: Ban, color: 'text-orange-600 bg-orange-50' },
  unblock:         { label: 'Desbloqueó a un usuario', icon: UserCheck, color: 'text-emerald-600 bg-emerald-50' },
  create_category: { label: 'Creó una categoría', icon: FolderPlus, color: 'text-cmic-600 bg-cmic-50' },
  delete_category: { label: 'Eliminó una categoría', icon: FolderX, color: 'text-rose-600 bg-rose-50' },
};

function LogInner() {
  const { lang } = useParams();
  const base = `/${lang}/${lang === 'en' ? 'forum' : 'foro'}`;
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/forum/moderation-log')
      .then(({ data }) => setRows(data || []))
      .catch((e) => { setErr(formatApiError(e.response?.data?.detail) || 'Error'); setRows([]); });
  }, []);

  return (
    <div data-testid="forum-moderation-log-page" className="bg-gray-50 min-h-[60vh] pt-20">
      <section className="bg-white border-b border-gray-200 py-5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to={`${base}?tab=foro`} className="inline-flex items-center gap-1.5 text-xs text-cmic-600 hover:text-cmic-700 mb-2">
            <ArrowLeft size={14} /> Foro interno
          </Link>
          <h1 className="text-xl sm:text-2xl font-bold text-charcoal-800 flex items-center gap-2">
            <ShieldAlert size={22} className="text-cmic-600" /> Registro de moderación
          </h1>
          <p className="text-sm text-charcoal-500 mt-1">Historial de acciones de moderación del foro (horario de la Ciudad de México).</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-4">{err}</div>}
        {rows === null ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-cmic-500" size={28} /></div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-charcoal-500">
            Aún no hay acciones de moderación registradas.
          </div>
        ) : (
          <ol className="space-y-2" data-testid="forum-moderation-log-list">
            {rows.map((r) => {
              const meta = ACTION_META[r.action] || { label: r.action, icon: ShieldAlert, color: 'text-slate-500 bg-slate-100' };
              const Icon = meta.icon;
              return (
                <li key={r.id} data-testid={`mod-log-${r.id}`}
                  className="bg-white border border-gray-200 rounded-xl p-4 flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                    <Icon size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-charcoal-800">
                      <strong>{r.actor_name || 'Moderador'}</strong> — {meta.label}
                      {r.target_author && <span className="text-charcoal-500"> de <strong className="text-charcoal-700">{r.target_author}</strong></span>}
                    </div>
                    {(r.thread_title || r.detail) && (
                      <div className="text-[12px] text-charcoal-500 mt-0.5">
                        {r.thread_title && <span>Hilo: “{r.thread_title}”</span>}
                        {r.thread_title && r.detail && <span> · </span>}
                        {r.detail && <span>{r.detail}</span>}
                      </div>
                    )}
                    <div className="text-[11px] text-charcoal-400 mt-1">{fullDate(r.created_at)}</div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </div>
  );
}

export default function ForumModerationLogPage() {
  return (
    <InternalAccessGuard>
      <LogInner />
    </InternalAccessGuard>
  );
}
